<script setup>
import { computed } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import { PhCheckCircle, PhClock, PhCreditCard, PhArrowSquareOut } from '@phosphor-icons/vue'
import Layout  from '@pages/Shared/layouts/ApplicantLayout.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'

const props = defineProps({
    applicant:           Object,
    application:         Object,
    payments:            Array,
    formFeeAmount:       Number,
    acceptanceFeeAmount: Number,
})

// ── Helpers ────────────────────────────────────────────────────────────────

const resolveEnum = (val) => {
    if (!val) return null
    if (typeof val === 'string') return val
    if (typeof val === 'object' && val.value) return val.value
    return String(val)
}

const naira = (val) =>
    '₦' + Number(val ?? 0).toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

// ── Payment status ─────────────────────────────────────────────────────────

const hasPaidFormFee = computed(() =>
    props.payments?.some(p => resolveEnum(p.category) === 'application_form_fee'
        && resolveEnum(p.status) === 'paid')
)

const hasPaidAcceptanceFee = computed(() =>
    props.payments?.some(p => resolveEnum(p.category) === 'acceptance_fee'
        && resolveEnum(p.status) === 'paid')
)

// ── Current due ────────────────────────────────────────────────────────────

// The single active payment due right now.
// Step 1 — form fee. Step 2 — acceptance fee (only after admitted).
const currentDue = computed(() => {
    if (!hasPaidFormFee.value) {
        return {
            category:    'application_form_fee',
            label:       'Application Form Fee',
            description: 'Required to access and submit your application form.',
            amount:      props.formFeeAmount,
            btnClass:    'bg-amber-500 hover:bg-amber-600',
        }
    }
    if (props.application?.status === 'admitted' && !hasPaidAcceptanceFee.value) {
        return {
            category:    'acceptance_fee',
            label:       'Acceptance Fee',
            description: 'Required to confirm your admission and proceed to enrollment.',
            amount:      props.acceptanceFeeAmount,
            btnClass:    'bg-green-600 hover:bg-green-700',
        }
    }
    return null
})

// ── Form — POST to /applicant/payments/initiate ────────────────────────────
//
// On success, the controller returns a redirect()->away($paystackUrl).
// Inertia follows the redirect, sending the user to Paystack directly.
// No PaystackPop. No inline JS SDK. No axios.

const payForm = useForm({ category: '' })

const pay = (category) => {
    payForm.category = category
    payForm.post('/applicant/payments/initiate', {
        // Inertia follows the server-side redirect()->away() automatically
        onError: () => {
            payForm.reset()
        },
    })
}

// ── Status colours ─────────────────────────────────────────────────────────

const statusColor = (val) => ({
    paid:    'bg-green-50 text-green-700',
    pending: 'bg-yellow-50 text-yellow-700',
    failed:  'bg-red-50 text-red-600',
}[resolveEnum(val)] ?? 'bg-gray-100 text-gray-500')
</script>

<template>
    <Layout>
        <Head title="Payments" />

        <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">Payments</h1>
            <p class="text-gray-500 text-sm mt-1">Manage your application payments</p>
        </div>

        <!-- ── Current due payment card ────────────────────────────────── -->
        <div v-if="currentDue" class="mb-8">
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div class="p-6">
                    <div class="flex items-start justify-between mb-4">
                        <div>
                            <h3 class="font-bold text-gray-800">{{ currentDue.label }}</h3>
                            <p class="text-sm text-gray-500 mt-0.5">{{ currentDue.description }}</p>
                        </div>
                        <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                                     bg-orange-50 text-orange-600 text-xs font-semibold">
                            <PhClock :size="13" />
                            Due Now
                        </span>
                    </div>

                    <p class="text-3xl font-bold text-gray-900 mb-6">
                        {{ naira(currentDue.amount) }}
                    </p>

                    <!--
                        Simple button — clicks POST to /applicant/payments/initiate.
                        Server creates the PENDING record, calls Paystack initialize(),
                        returns redirect()->away($authorizationUrl).
                        Inertia follows the redirect to Paystack automatically.
                        On completion, Paystack redirects back to /applicant/payments/callback.
                    -->
                    <button
                        @click="pay(currentDue.category)"
                        :disabled="payForm.processing"
                        class="w-full flex items-center justify-center gap-2 py-3 rounded-xl
                               text-white text-sm font-semibold transition shadow
                               disabled:opacity-60 disabled:cursor-not-allowed"
                        :class="currentDue.btnClass"
                    >
                        <Spinner v-if="payForm.processing" :size="14" />
                        <PhArrowSquareOut v-else :size="16" />
                        <span v-if="payForm.processing">Redirecting to Paystack…</span>
                        <span v-else>Pay {{ naira(currentDue.amount) }}</span>
                    </button>

                    <p class="text-xs text-gray-400 text-center mt-3">
                        You will be redirected to Paystack to complete your payment securely.
                    </p>
                </div>
            </div>
        </div>

        <!-- ── Nothing due ─────────────────────────────────────────────── -->
        <div v-else class="mb-8">
            <div class="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center gap-4">
                <PhCheckCircle :size="28" class="text-green-600 shrink-0" />
                <div>
                    <p class="font-bold text-green-800">All payments up to date</p>
                    <p class="text-sm text-green-600 mt-0.5">No outstanding payments at this time.</p>
                </div>
            </div>
        </div>

        <!-- ── Payment History ─────────────────────────────────────────── -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100">
                <h4 class="font-bold text-gray-800">Payment History</h4>
            </div>

            <div v-if="payments?.length" class="divide-y divide-gray-100">
                <div v-for="p in payments" :key="p.id"
                    class="px-6 py-4 flex items-center justify-between gap-4">
                    <div>
                        <p class="text-sm font-semibold text-gray-800 capitalize">
                            {{ (resolveEnum(p.category) ?? '').replace(/_/g, ' ') }}
                        </p>
                        <p class="text-xs text-gray-400 mt-0.5 font-mono">
                            {{ p.reference }}
                        </p>
                        <p class="text-xs text-gray-400 mt-0.5">
                            {{ (resolveEnum(p.payment_method) ?? '').replace(/_/g, ' ') }}
                            <template v-if="p.paid_at">
                                · {{ new Date(p.paid_at).toLocaleDateString('en-NG', {
                                    day: '2-digit', month: 'short', year: 'numeric'
                                }) }}
                            </template>
                        </p>
                    </div>
                    <div class="text-right shrink-0">
                        <p class="text-sm font-bold text-gray-900">
                            {{ naira(p.amount_paid) }}
                        </p>
                        <span
                            class="inline-flex items-center px-2 py-0.5 rounded-full
                                   text-xs font-medium capitalize mt-1"
                            :class="statusColor(p.status)"
                        >
                            {{ resolveEnum(p.status) }}
                        </span>
                    </div>
                </div>
            </div>

            <div v-else class="px-6 py-12 text-center text-sm text-gray-400">
                No payments recorded yet.
            </div>
        </div>
    </Layout>
</template>